/**
 * RoleFunctionsDialog
 *
 * Role Setup → per-FUNCTION grants. Page permissions answer "can this role see
 * this page"; functions answer "may this role perform this action" — e.g.
 * `training.view_team_records`, which lets a manager see the training records
 * of everyone below them in the reporting line (TRUS-592).
 *
 * Backed by endpoints that existed before this screen did:
 *   GET  /api/organizations/{orgId}/roles/{roleType}/functions
 *   PUT  /api/organizations/{orgId}/roles/{roleType}/functions
 * GET lists EVERY known function key with whether this role holds it, so a key
 * added on the server shows up here without a frontend change. Every function
 * is fail-closed: off until granted.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { apiClient } from '@/services/apiClient';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { RefreshCw, Save, AlertCircle, KeyRound } from 'lucide-react';
import type { OrganizationRole } from '@/types/roleTerminology';

interface FunctionRow {
  function_key: string;
  description: string;
  enabled: boolean;
}

interface RoleFunctionsDialogProps {
  organizationId: number;
  role: OrganizationRole | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Readable names for the keys. A key without one still shows, by its key. */
const FUNCTION_LABELS: Record<string, [string, string]> = {
  'training.view_team_records': ['admin.roleFunctions.labels.view_team_records', 'View team training records'],
};

export function RoleFunctionsDialog({ organizationId, role, open, onOpenChange }: RoleFunctionsDialogProps) {
  const { t } = useAppTranslation();
  const labelFor = useCallback(
    (key: string): string => {
      const label = FUNCTION_LABELS[key];
      return label ? t(label[0], label[1]) : key;
    },
    [t],
  );
  const [rows, setRows] = useState<FunctionRow[]>([]);
  const [initial, setInitial] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cboHeader = { headers: { 'X-CBO-ID': organizationId.toString() } };

  const load = useCallback(async () => {
    if (!role) return;
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.get(
        `/api/organizations/${organizationId}/roles/${role.role_type}/functions`,
        cboHeader,
      );
      const list: FunctionRow[] = (res.data || []).slice().sort((a: FunctionRow, b: FunctionRow) =>
        labelFor(a.function_key).localeCompare(labelFor(b.function_key)),
      );
      setRows(list);
      setInitial(Object.fromEntries(list.map((r) => [r.function_key, r.enabled])));
    } catch (err: any) {
      console.error('Error loading role functions:', err);
      setError(err.response?.data?.detail || t('admin.roleFunctions.loadFailed', 'Failed to load functions'));
    } finally {
      setLoading(false);
    }
  }, [role, organizationId, labelFor, t]);

  useEffect(() => {
    if (open && role) load();
  }, [open, role, load]);

  const setEnabled = (key: string, enabled: boolean) => {
    setRows((prev) => prev.map((r) => (r.function_key === key ? { ...r, enabled } : r)));
  };

  // Send only what changed, so saving cannot rewrite a grant nobody touched.
  const changed = rows.filter((r) => initial[r.function_key] !== r.enabled);

  const handleSave = async () => {
    if (!role || changed.length === 0) return;
    try {
      setSaving(true);
      setError(null);
      await apiClient.put(
        `/api/organizations/${organizationId}/roles/${role.role_type}/functions`,
        { functions: changed.map((r) => ({ function_key: r.function_key, enabled: r.enabled })) },
        cboHeader,
      );
      onOpenChange(false);
    } catch (err: any) {
      console.error('Error saving role functions:', err);
      setError(err.response?.data?.detail || t('admin.roleFunctions.saveFailed', 'Failed to save functions'));
    } finally {
      setSaving(false);
    }
  };

  const grantedCount = rows.filter((r) => r.enabled).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            {t('admin.roleFunctions.title', 'Functions — {{role}}', { role: role?.display_name_en || role?.role_type })}
          </DialogTitle>
          <DialogDescription>
            {t(
              'admin.roleFunctions.description',
              'Specific actions this role may perform, beyond the pages it can see. Everyone with this role gets what is turned on here. All functions are off until granted.',
            )}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            {t('admin.roleFunctions.loading', 'Loading functions…')}
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">{t('admin.roleFunctions.empty', 'No functions are defined.')}</div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
              <span>{t('admin.roleFunctions.count', '{{count}} functions', { count: rows.length })}</span>
              <Badge variant="secondary">
                {t('admin.roleFunctions.granted', '{{count}} granted', { count: grantedCount })}
              </Badge>
            </div>
            {rows.map((r) => (
              <label
                key={r.function_key}
                className="flex items-start justify-between gap-4 rounded-md border p-3 cursor-pointer"
              >
                <div className="min-w-0">
                  <p className="font-medium">{labelFor(r.function_key)}</p>
                  <p className="text-xs text-muted-foreground font-mono">{r.function_key}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{r.description}</p>
                </div>
                {/* TRUS-766: the shared Switch takes its "on" colour from org branding,
                    which is near-black or missing for some orgs, so on and off looked the
                    same. Explicit colours plus an On/Off word make the state readable. */}
                <div className="flex shrink-0 items-center gap-2">
                  <span
                    className={`w-20 text-right text-xs font-medium ${r.enabled ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground'}`}
                  >
                    {r.enabled ? t('admin.roleFunctions.on', 'On') : t('admin.roleFunctions.off', 'Off')}
                  </span>
                  <Switch
                    checked={r.enabled}
                    onCheckedChange={(v) => setEnabled(r.function_key, v)}
                    aria-label={labelFor(r.function_key)}
                    className="data-[state=checked]:bg-emerald-600 data-[state=unchecked]:bg-gray-300 dark:data-[state=unchecked]:bg-gray-600"
                  />
                </div>
              </label>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving || loading || changed.length === 0}>
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {t('common.save', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RoleFunctionsDialog;
